import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import FiltersCard from "../../components/FiltersCard";
import MultiSelectComponent from "../../components/MultiSelect";
import PoliciesAndDivisions from "../reports/PoliciesAndDivisions";
import {
    CONVERGE_TYPE,
    REPORTING_PERIOD,
    COMPARE_YOUR_COMPANY_TO,
    ANALYSIS_TYPE,
    REPORTING_GROUP,
    POLICIES_AND_DIVISIONS,
    POLICY_DIVISION_ELIGIBLE_COVERAGE_TYPES,
} from "../../constants";
import QuarterlyRangePicker from "./QuarterlyRangePicker";
import GTM from "../../GTM";

const VizFilters = ({
    organizationName,
    relatedOrganizationNames,
    userSelectedOrganizationReports,
    reportId,
    enableCoverageType,
    enableCompareYourCompanyTo,
    enableAnalysisType,
    enableReportingGroup,
    selectedCompanyYourCompanyTo,
    selectedAnalysisType,
    selectedReportingGroup,
    initialCompareYourCompanyto,
    initialAnalysisTypeFilter,
    initialReportingGroup,
    resetFilters,
    setResetFilters,
    setSelectedCoverageType,
    setSelectedCompanyYourCompanyTo,
    setSelectedAnalysisType,
    setSelectedReportingGroup,
    selectedCoverageType,
    reportParametersOptions,
    initialCoverageFilter,
    selectedReportingPeriod,
    setSelectedReportingPeriod,
    setApplyFilter,
    enablePolicyandDivisisonFilter,
    allowedPoliciesAndDivisions,
    selectedPolicyDivision,
    setSelectedPolicyDivision,
    initialPolicyDivision,
}) => {
    const gtm = new GTM(null);
    const [tempSelectedCoverageType, setTempSelectedCoverageType] =
        useState(selectedCoverageType);
    const [enableResetFilter, setEnableResetFilters] = useState(false);
    const [enableApplyFilter, setEnableApplyFilter] = useState(false);
    const [tempSelectedAnalysisType, setTempSelectedAnalysisType] =
        useState(selectedAnalysisType);
    const [tempSelectedReportingGroup, setTempSelectedReportingGroup] =
        useState(selectedReportingGroup);
    const [
        tempSelectedCompanyYourCompanyTo,
        setTempSelectedCompanyYourCompanyTo,
    ] = useState(selectedCompanyYourCompanyTo);
    const [tempSelectedPolicyDivision, setTempSelectedPolicyDivision] =
        useState(selectedPolicyDivision);
    const [searchTerm, setSearchTerm] = useState("");

    const {
        coverageTypeOptions,
        compareYourCompanyTo,
        analysisTypeOptions,
        reportingGroupOptions,
        reportingPeriodOptions,
    } = useMemo(() => {
        const getOptions = (name) =>
            reportParametersOptions.find(
                (param) => param.parameterName === name
            )?.options || [];

        return {
            coverageTypeOptions: getOptions(CONVERGE_TYPE),
            compareYourCompanyTo: getOptions(COMPARE_YOUR_COMPANY_TO),
            analysisTypeOptions: getOptions(ANALYSIS_TYPE),
            reportingGroupOptions: getOptions(REPORTING_GROUP),
            reportingPeriodOptions: getOptions(REPORTING_PERIOD),
        };
    }, [reportParametersOptions]);

    const handleResetFilters = () => {
        setTempSelectedCoverageType(initialCoverageFilter);
        setTempSelectedCompanyYourCompanyTo(initialCompareYourCompanyto);
        setTempSelectedAnalysisType(initialAnalysisTypeFilter);
        setTempSelectedReportingGroup(initialReportingGroup);
        setTempSelectedPolicyDivision(initialPolicyDivision);
        setSelectedReportingPeriod({
            name: "",
            displayName: "",
        });
        setApplyFilter(false);
        setEnableResetFilters(false);
        setEnableApplyFilter(true);
    };
    useEffect(() => {
        if (resetFilters) {
            setTempSelectedCoverageType(initialCoverageFilter);
            setTempSelectedCompanyYourCompanyTo(initialCompareYourCompanyto);
            setTempSelectedAnalysisType(initialAnalysisTypeFilter);
            setTempSelectedReportingGroup(initialReportingGroup);
            setTempSelectedPolicyDivision(initialPolicyDivision);
            setSelectedReportingPeriod({
                name: "",
                displayName: "",
            });
            setEnableResetFilters(false);
            setResetFilters(false);
            setApplyFilter(false);
            setEnableApplyFilter(false);
        }
    }, [resetFilters]);

    const filteredPoliciesAndDivisions = useMemo(() => {
        if (!allowedPoliciesAndDivisions) return [];
        if (searchTerm.length < 3) return allowedPoliciesAndDivisions;
        return allowedPoliciesAndDivisions.filter((item) => {
            return (
                item.policyId
                    ?.toLowerCase()
                    .includes(searchTerm.toLowerCase()) ||
                item.divisions.some(
                    (division) =>
                        division.divisionName
                            ?.toLowerCase()
                            .includes(searchTerm.toLowerCase()) ||
                        division.divisionId
                            ?.toLowerCase()
                            .includes(searchTerm.toLowerCase())
                )
            );
        });
    }, [allowedPoliciesAndDivisions, searchTerm]);

    const isPolicyDivisionEligibleCoverageSelected =
        tempSelectedCoverageType.some((coverage) =>
            POLICY_DIVISION_ELIGIBLE_COVERAGE_TYPES.includes(coverage.value)
        );
    const showPolicyDivisionFilter =
        enablePolicyandDivisisonFilter &&
        isPolicyDivisionEligibleCoverageSelected;

    const validPolicyDivisionCount = useMemo(() => {
        if (!showPolicyDivisionFilter) return 1;
        const validValues = new Set(
            (allowedPoliciesAndDivisions || []).flatMap((item) => [
                item.policyId,
                ...(item.divisions || []).map(
                    (d) => `${item.policyId}-${d.divisionId}`
                ),
            ])
        );
        return tempSelectedPolicyDivision.filter((v) => validValues.has(v))
            .length;
    }, [
        showPolicyDivisionFilter,
        allowedPoliciesAndDivisions,
        tempSelectedPolicyDivision,
    ]);

    const handlePolicyCheckboxChange = (policyId) => {
        const policyDivisions =
            filteredPoliciesAndDivisions.find(
                (item) => item.policyId === policyId
            )?.divisions || [];
        const divisionIds = policyDivisions.map(
            (division) => `${policyId}-${division.divisionId}`
        );

        setTempSelectedPolicyDivision((prevSelected) => {
            if (prevSelected.includes(policyId)) {
                return prevSelected.filter(
                    (item) => item !== policyId && !divisionIds.includes(item)
                );
            }
            return [...new Set([...prevSelected, policyId, ...divisionIds])];
        });
        setEnableResetFilters(true);
        setEnableApplyFilter(true);
    };

    const handleDivisionCheckboxChange = (divisionId, policyId) => {
        setTempSelectedPolicyDivision((prevSelected) => {
            const policyDivisions =
                filteredPoliciesAndDivisions
                    .find((item) => item.policyId === policyId)
                    ?.divisions.map(
                        (division) => `${policyId}-${division.divisionId}`
                    ) || [];

            const newSelected = prevSelected.includes(divisionId)
                ? prevSelected.filter((item) => item !== divisionId)
                : [...prevSelected, divisionId];

            const allPolicyDivisionsSelected = policyDivisions.every((id) =>
                newSelected.includes(id)
            );

            if (allPolicyDivisionsSelected) {
                return [...new Set([...newSelected, policyId])];
            }
            return newSelected.filter((item) => item !== policyId);
        });
        setEnableResetFilters(true);
        setEnableApplyFilter(true);
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const clearSearch = () => {
        setSearchTerm("");
    };

    const handleSelectAll = () => {
        const allItems = filteredPoliciesAndDivisions.flatMap((item) => [
            item.policyId,
            ...item.divisions.map(
                (division) => `${item.policyId}-${division.divisionId}`
            ),
        ]);
        setTempSelectedPolicyDivision(allItems);
        setEnableResetFilters(true);
        setEnableApplyFilter(true);
    };

    const handleUnselectAll = () => {
        setTempSelectedPolicyDivision([]);
        setEnableResetFilters(true);
        setEnableApplyFilter(true);
    };

    const coverage_type = tempSelectedCoverageType
        .map((item) => item.value)
        .join(", ");
    const compare_your_company_to = tempSelectedCompanyYourCompanyTo
        .map((item) => item.value)
        .join(", ");
    const isLeaveCoverageSelected = tempSelectedCoverageType.some((coverage) =>
        [coverage.value, coverage.label, coverage.name].some(
            (value) =>
                typeof value === "string" && value.toLowerCase() === "leave"
        )
    );

    const handleApplyFilters = async () => {
        setSelectedCoverageType(tempSelectedCoverageType);
        setSelectedCompanyYourCompanyTo(tempSelectedCompanyYourCompanyTo);
        setSelectedAnalysisType(tempSelectedAnalysisType);
        setSelectedReportingGroup(tempSelectedReportingGroup);
        setSelectedPolicyDivision(tempSelectedPolicyDivision);
        setApplyFilter(true);
        setEnableApplyFilter(false);
        gtm.Event(
            "custom_event",
            "button",
            "click",
            "apply filters",
            "report detail",
            "trends and benchmarking",
            "apply_filters",
            "undefined",
            selectedReportingPeriod?.name,
            coverage_type,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            compare_your_company_to
        );
    };

    const multiSelectFilters = [
        {
            enabled: enableCoverageType,
            header: CONVERGE_TYPE,
            options: coverageTypeOptions,
            selected: tempSelectedCoverageType, // Use temp state
            setSelected: setTempSelectedCoverageType,
            label: CONVERGE_TYPE,
            showCheckboxes: false,
            enableSearch: false,
        },
        {
            enabled: enableAnalysisType,
            header: ANALYSIS_TYPE,
            options: analysisTypeOptions,
            selected: tempSelectedAnalysisType,
            setSelected: setTempSelectedAnalysisType,
            label: ANALYSIS_TYPE,
            showCheckboxes: false,
            enableSearch: false,
        },
        ...(enableReportingGroup && isLeaveCoverageSelected
            ? [
                  {
                      enabled: true,
                      header: REPORTING_GROUP,
                      options: reportingGroupOptions,
                      selected: tempSelectedReportingGroup,
                      setSelected: setTempSelectedReportingGroup,
                      label: REPORTING_GROUP,
                      showCheckboxes: true,
                      enableSearch: true,
                  },
              ]
            : []),
        ...(showPolicyDivisionFilter
            ? [
                  {
                      enabled: true,
                      label: POLICIES_AND_DIVISIONS,
                      header: POLICIES_AND_DIVISIONS,
                  },
              ]
            : []),
        {
            label: REPORTING_PERIOD,
            header: REPORTING_PERIOD,
        },
        {
            enabled: enableCompareYourCompanyTo,
            header: COMPARE_YOUR_COMPANY_TO,
            options: compareYourCompanyTo,
            selected: tempSelectedCompanyYourCompanyTo, // Use temp state
            setSelected: setTempSelectedCompanyYourCompanyTo,
            label: COMPARE_YOUR_COMPANY_TO,
            enableSearch: false,
        },
    ];

    const actionsButtonPosition = multiSelectFilters.length % 4 === 0;

    const isApplyDisabled = useMemo(() => {
        if (enableCoverageType && tempSelectedCoverageType.length === 0) {
            return true;
        }

        if (
            enableCompareYourCompanyTo &&
            tempSelectedCompanyYourCompanyTo.length === 0
        ) {
            return true;
        }

        if (enableAnalysisType && tempSelectedAnalysisType.length === 0) {
            return true;
        }

        if (
            enableReportingGroup &&
            isLeaveCoverageSelected &&
            tempSelectedReportingGroup.length === 0
        ) {
            return true;
        }

        if (showPolicyDivisionFilter && validPolicyDivisionCount === 0) {
            return true;
        }

        if (!selectedReportingPeriod?.displayName) {
            return true;
        }

        return false;
    }, [
        enableCoverageType,
        tempSelectedCoverageType.length,
        enableCompareYourCompanyTo,
        tempSelectedCompanyYourCompanyTo,
        enableAnalysisType,
        tempSelectedAnalysisType.length,
        enableReportingGroup,
        isLeaveCoverageSelected,
        tempSelectedReportingGroup.length,
        showPolicyDivisionFilter,
        validPolicyDivisionCount,
        selectedReportingPeriod,
    ]);

    return (
        <FiltersCard
            organizationName={organizationName}
            relatedOrganizationNames={relatedOrganizationNames}
            userSelectedOrganizationReports={userSelectedOrganizationReports}
            reportId={reportId}
            actionsButtonPosition={actionsButtonPosition}
            enableresetFilter={enableResetFilter}
            handleResetFilters={handleResetFilters}
            isApplyDisabled={isApplyDisabled}
            applyFilter={enableApplyFilter}
            handleApplyFilters={handleApplyFilters}
        >
            {multiSelectFilters.map(
                ({
                    enabled,
                    header,
                    options,
                    selected,
                    setSelected,
                    label,
                    showCheckboxes,
                    enableSearch,
                }) => {
                    if (
                        label === REPORTING_PERIOD ||
                        header === REPORTING_PERIOD
                    ) {
                        return (
                            <QuarterlyRangePicker
                                key={label}
                                selectedReportingPeriod={
                                    selectedReportingPeriod
                                }
                                setSelectedReportingPeriod={
                                    setSelectedReportingPeriod
                                }
                                setEnableResetFilters={setEnableResetFilters}
                                setApplyFilter={setEnableApplyFilter}
                                reportingPeriodOptions={reportingPeriodOptions}
                            />
                        );
                    }
                    if (label === POLICIES_AND_DIVISIONS) {
                        return (
                            <PoliciesAndDivisions
                                key={label}
                                label={POLICIES_AND_DIVISIONS}
                                filteredPoliciesAndDivisions={
                                    filteredPoliciesAndDivisions
                                }
                                selectedPolicyDivision={
                                    tempSelectedPolicyDivision
                                }
                                handleSelectAll={handleSelectAll}
                                handleUnselectAll={handleUnselectAll}
                                searchTerm={searchTerm}
                                handleSearchChange={handleSearchChange}
                                clearSearch={clearSearch}
                                handlePolicyCheckboxChange={
                                    handlePolicyCheckboxChange
                                }
                                handleDivisionCheckboxChange={
                                    handleDivisionCheckboxChange
                                }
                                attribute2={"trends and benchmarking"}
                            />
                        );
                    }
                    return (
                        enabled && (
                            <MultiSelectComponent
                                key={label}
                                headerLabel={header}
                                options={options}
                                selectedOptions={selected}
                                setSelectedOptions={setSelected}
                                gtmTagLabel={label}
                                showCheckboxes={showCheckboxes}
                                setEnableReset={setEnableResetFilters}
                                setEnableApplyButton={setEnableApplyFilter}
                                enableSearch={enableSearch}
                                handleGtmTags={() => {}}
                                attribute2={""}
                                applyFilter={enableApplyFilter}
                            />
                        )
                    );
                }
            )}
        </FiltersCard>
    );
};

VizFilters.propTypes = {
    organizationName: PropTypes.string.isRequired,
    relatedOrganizationNames: PropTypes.string,
    userSelectedOrganizationReports: PropTypes.object.isRequired,
    reportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    enableCoverageType: PropTypes.bool,
    enableCompareYourCompanyTo: PropTypes.bool,
    enableAnalysisType: PropTypes.bool,
    enableReportingGroup: PropTypes.bool,
    selectedCompanyYourCompanyTo: PropTypes.array,
    selectedAnalysisType: PropTypes.array,
    selectedReportingGroup: PropTypes.array,
    initialCompareYourCompanyto: PropTypes.array,
    initialAnalysisTypeFilter: PropTypes.array,
    initialReportingGroup: PropTypes.array,
    resetFilters: PropTypes.bool,
    setResetFilters: PropTypes.func,
    setSelectedCoverageType: PropTypes.func,
    setSelectedCompanyYourCompanyTo: PropTypes.func,
    setSelectedAnalysisType: PropTypes.func,
    setSelectedReportingGroup: PropTypes.func,
    selectedCoverageType: PropTypes.array,
    reportParametersOptions: PropTypes.array,
    initialCoverageFilter: PropTypes.array,
    selectedReportingPeriod: PropTypes.array,
    setSelectedReportingPeriod: PropTypes.array,
    onApply: PropTypes.func,
    enablePolicyandDivisisonFilter: PropTypes.bool,
    allowedPoliciesAndDivisions: PropTypes.array,
    selectedPolicyDivision: PropTypes.array,
    setSelectedPolicyDivision: PropTypes.func,
    initialPolicyDivision: PropTypes.array,
};

export default VizFilters;
